import { createSerializer, parseAsJson } from 'nuqs';

import type { SearchQuery } from '~/features/advanced-search/types';
import type { FilterQueryEntity } from '~/types/OpenSearch';

import { AND } from '~/common/constants/openSearch';
import { DETAILS_PANEL_TABS } from '~/common/layout/Toolbar';
import { QueryParamType } from '~/enums/QueryParamType';

// Long-standing safe ceiling for a full URL across browsers, proxies, and
// servers (older IE topped out at 2083).
const MAX_URL_LENGTH = 2000;

const DOCUMENT_HANDLER_ID_FILTER = 'document_handler_id_Document';

const parseSearchQuery = (value: unknown): SearchQuery | null =>
  Array.isArray(value) ? value : null;

const serializeKeywordMatchesQuery = createSerializer({
  [QueryParamType.KeywordMatchesQuery]: parseAsJson(parseSearchQuery),
});

interface AppendOptions {
  /**
   * Docked panel the surface being linked from currently has open, straight from
   * `useActiveToolbarPanelParam`. The param is only carried when that panel is
   * Keyword Matches: the panel the user is reading is the only evidence that
   * matches are what they followed the result for, and the document page both
   * shows and opens its Keyword Matches panel off the param's presence.
   */
  activePanelId?: string | null;
}

/**
 * Appends the current search's query to `url` as a JSON query param, so the
 * standalone document page (opened in a new tab, with no shared state) can
 * re-run this same search scoped to just that document and show its actual
 * keyword matches, fetched fresh rather than carried across in the URL.
 * Omits the param entirely if it would push the URL past a safe length —
 * unlike match snippets, a query can't be safely truncated without changing
 * what it means, so there's nothing to clip.
 */
export function appendKeywordMatchesQueryParam(
  url: string,
  query: SearchQuery | undefined,
  { activePanelId }: AppendOptions = {},
): string {
  if (!query?.length || activePanelId !== DETAILS_PANEL_TABS.matches)
    return url;

  const candidate = serializeKeywordMatchesQuery(url, {
    [QueryParamType.KeywordMatchesQuery]: query,
  });
  return candidate.length <= MAX_URL_LENGTH ? candidate : url;
}

/**
 * ANDs a filter for `docHandlerId` onto `query`, so re-running the search
 * with the result scopes to just that one document. Uses
 * `document_handler_id_Document` (the "Document Id" filter) since that's the
 * only id available on the document page — the route param, not the
 * evisearch item id (`document_id_Document`) a search result carries.
 */
export function scopeQueryToDocumentHandler(
  query: SearchQuery,
  docHandlerId: string,
): SearchQuery {
  const handlerFilter: FilterQueryEntity = {
    type: 'filter',
    id: DOCUMENT_HANDLER_ID_FILTER,
    operator: 'contains_any',
    value: { value_list: [docHandlerId], value_meta: {} },
  };
  return query.length ? [...query, AND, handlerFilter] : [handlerFilter];
}
